import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { mfaEnrollConfirm, mfaEnrollStart, mfaVerify, type MfaEnrollmentStart } from '../api/auth';

interface Props {
  mode: 'verify' | 'enroll';
  onDone: () => Promise<void>;
  onCancel: () => void;
}

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'MFA_INVALID_CODE') return 'Kód není platný. Zkuste to znovu.';
    if (e.code === 'MFA_CHALLENGE_EXPIRED') return 'Platnost přihlášení vypršela, přihlaste se znovu.';
    if (e.code === 'RATE_LIMITED') return 'Příliš mnoho pokusů, chvíli počkejte.';
    if (e.code === 'MFA_UNAVAILABLE') return 'Dvoufázové ověření není na serveru nastaveno.';
    return e.message || 'Ověření selhalo.';
  }
  return 'Ověření selhalo.';
}

export function MfaStep({ mode, onDone, onCancel }: Props) {
  return mode === 'enroll' ? <EnrollWizard onDone={onDone} onCancel={onCancel} /> : <VerifyForm onDone={onDone} onCancel={onCancel} />;
}

function VerifyForm({ onDone, onCancel }: Omit<Props, 'mode'>) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [useBackup, setUseBackup] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await mfaVerify(code.trim());
      await onDone();
    } catch (err) {
      setError(describeError(err));
      if (err instanceof ApiError && err.code === 'MFA_CHALLENGE_EXPIRED') onCancel();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="login__card" onSubmit={onSubmit}>
      <img className="login__logo" src="/assets/platform1-logo.png" alt="Platform 1" />
      <p className="login__hint">{useBackup ? 'Zadejte jeden ze záložních kódů.' : 'Zadejte kód z ověřovací aplikace.'}</p>
      <label className="login__field">
        <span>{useBackup ? 'Záložní kód' : 'Ověřovací kód'}</span>
        <input
          type="text"
          inputMode={useBackup ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          autoFocus
          required
          maxLength={useBackup ? 11 : 6}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={busy}
        />
      </label>
      {error && <p className="login__error" role="alert">{error}</p>}
      <button className="login__submit" type="submit" disabled={busy || !code.trim()}>
        {busy ? 'Ověřuji…' : 'Ověřit'}
      </button>
      <div className="login__links">
        <button type="button" className="login__link" onClick={() => { setUseBackup((v) => !v); setCode(''); setError(null); }}>
          {useBackup ? 'Použít kód z aplikace' : 'Nemám přístup k aplikaci - použít záložní kód'}
        </button>
        <button type="button" className="login__link" onClick={onCancel}>Zpět na přihlášení</button>
      </div>
    </form>
  );
}

function EnrollWizard({ onDone, onCancel }: Omit<Props, 'mode'>) {
  const [start, setStart] = useState<MfaEnrollmentStart | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    mfaEnrollStart()
      .then(setStart)
      .catch((e) => {
        setError(describeError(e));
        if (e instanceof ApiError && e.code === 'MFA_CHALLENGE_EXPIRED') onCancel();
      });
  }, [onCancel]);

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await mfaEnrollConfirm(code.trim());
      setBackupCodes(r.backupCodes);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  if (backupCodes) {
    return (
      <div className="login__card login__card--wide">
        <img className="login__logo" src="/assets/platform1-logo.png" alt="Platform 1" />
        <h2 className="login__title">Záložní kódy</h2>
        <p className="login__hint">
          Uložte si je na bezpečné místo. Každý kód lze použít jednou, když nebudete mít přístup k ověřovací aplikaci. Už se nezobrazí.
        </p>
        <ul className="login__codes">
          {backupCodes.map((c) => <li key={c}><code>{c}</code></li>)}
        </ul>
        <label className="login__check">
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
          <span>Kódy jsem si uložil/a</span>
        </label>
        <button className="login__submit" type="button" disabled={!saved} onClick={() => void onDone()}>
          Pokračovat do aplikace
        </button>
      </div>
    );
  }

  return (
    <form className="login__card login__card--wide" onSubmit={confirm}>
      <img className="login__logo" src="/assets/platform1-logo.png" alt="Platform 1" />
      <h2 className="login__title">Nastavení dvoufázového ověření</h2>
      <p className="login__hint">
        Přihlášení do Platform 1 vyžaduje druhý faktor. Naskenujte QR kód v ověřovací aplikaci (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden…) a zadejte vygenerovaný kód.
      </p>
      {start ? (
        <div className="login__qr-row">
          <div className="login__qr" dangerouslySetInnerHTML={{ __html: start.qrSvg }} />
          <div className="login__secret">
            <span>Ruční zadání</span>
            <code>{start.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
          </div>
        </div>
      ) : (
        <p className="login__hint">{error ? '' : 'Připravuji…'}</p>
      )}
      <label className="login__field">
        <span>Kód z aplikace</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={busy || !start}
        />
      </label>
      {error && <p className="login__error" role="alert">{error}</p>}
      <button className="login__submit" type="submit" disabled={busy || !start || code.trim().length !== 6}>
        {busy ? 'Ověřuji…' : 'Aktivovat a přihlásit'}
      </button>
      <div className="login__links">
        <button type="button" className="login__link" onClick={onCancel}>Zpět na přihlášení</button>
      </div>
    </form>
  );
}
